'use client';

import { useEffect, useRef } from 'react';

import { cn } from '#utils';

// ---------------------------------------------------------------------------
// Shared Animation Library — @kit/ui/animations
// ---------------------------------------------------------------------------
// Reusable animation primitives for the design system.
// All keyframes are injected once globally and shared across components.
// ---------------------------------------------------------------------------

// ---- Global keyframe injection --------------------------------------------

const injected = new Set<string>();

function injectKeyframes(id: string, css: string) {
  if (injected.has(id)) return;
  injected.add(id);
  if (typeof document === 'undefined') return;
  if (document.getElementById(id)) return;
  const style = document.createElement('style');
  style.id = id;
  style.textContent = css;
  document.head.appendChild(style);
}

/** Hook: inject keyframes once on mount */
export function useInjectKeyframes(id: string, css: string) {
  const ref = useRef(false);
  useEffect(() => {
    if (ref.current) return;
    ref.current = true;
    injectKeyframes(id, css);
  }, [id, css]);
}

// ---- Shimmer / Liquid gradient animation ----------------------------------

const SHIMMER_KEYFRAMES = `
  @keyframes ui-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
`;

export interface LiquidBarProps {
  /** Fill percentage 0-100 */
  fill: number;
  /** Gradient color stops (CSS) */
  gradient?: string;
  /** Whether the liquid animation is active */
  animate?: boolean;
  /** Animation duration in seconds */
  duration?: number;
  /** Height class (default: h-1.5) */
  heightClass?: string;
  /** Track background class */
  trackClass?: string;
  /** Extra classes on the fill bar */
  fillClass?: string;
  /** Extra classes on the outer wrapper */
  className?: string;
}

/**
 * LiquidBar — Animated gradient progress/fill bar
 *
 * A horizontal bar that fills to a given percentage with a shimmering
 * gradient animation. The animation can be always-on or toggled (e.g.
 * on parent hover via CSS group).
 *
 * Usage:
 *   <LiquidBar fill={65} animate />
 *
 *   // Animate only on parent hover (parent needs `group` class):
 *   <LiquidBar fill={65} fillClass="group-hover:animate-shimmer" />
 */
export function LiquidBar({
  fill,
  gradient = 'linear-gradient(90deg, #6366f1, #8b5cf6, #a855f7, #6366f1)',
  animate = false,
  duration = 1.5,
  heightClass = 'h-1.5',
  trackClass = 'bg-black/5 dark:bg-white/10',
  fillClass,
  className,
}: LiquidBarProps) {
  useInjectKeyframes('ui-shimmer-keyframes', SHIMMER_KEYFRAMES);

  const clampedFill = Math.min(Math.max(fill, 0), 100);

  return (
    <div className={cn('overflow-hidden rounded-full', heightClass, trackClass, className)}>
      <div
        className={cn('h-full rounded-full transition-all', fillClass)}
        style={{
          width: `${clampedFill}%`,
          background: gradient,
          backgroundSize: '200% 100%',
          animation: animate
            ? `ui-shimmer ${duration}s linear infinite`
            : 'none',
        }}
      />
    </div>
  );
}

// ---- CSS class constants for group-hover animation -----------------------

/**
 * Inject a style rule that activates shimmer on group hover.
 * Call once with a group name and target class to enable
 * `.group/<name>:hover .target-class { animation: ... }`.
 */
export function useGroupHoverShimmer(
  groupName: string,
  targetClass: string,
  duration = 1.5,
) {
  const id = `ui-group-hover-shimmer-${groupName}-${targetClass}`;
  const css = `
    ${SHIMMER_KEYFRAMES}
    .group\\/${groupName}:hover .${targetClass} {
      animation: ui-shimmer ${duration}s linear infinite;
    }
  `;
  useInjectKeyframes(id, css);
}
